export const GLOBAL_MARKET_CODE = "GLOBAL";

export type PublicDomainKind = "public";

export type PublicDomainConfig = {
  kind: PublicDomainKind;
  marketCountryCode: string;
  defaultLocale: string;
  supportedLocales: readonly string[];
  hreflangByLocale: Record<string, string>;

  /**
   * Deprecated compatibility alias.
   * Use defaultLocale instead.
   */
  locale: string;

  /**
   * Deprecated compatibility alias.
   * Use hreflangByLocale[locale] instead.
   */
  hreflang: string;
};

type PublicDomainConfigInput = {
  marketCountryCode: string;
  defaultLocale: string;
  supportedLocales: readonly string[];
  hreflangByLocale: Record<string, string>;
};

function definePublicDomain(
  input: PublicDomainConfigInput,
): PublicDomainConfig {
  return {
    kind: "public",
    ...input,
    locale: input.defaultLocale,
    hreflang:
      input.hreflangByLocale[input.defaultLocale] || input.defaultLocale,
  };
}

export const domainConfig: Record<string, PublicDomainConfig> = {
  // Global hub
  "doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "en",
    supportedLocales: ["en"],
    hreflangByLocale: { en: "en" },
  }),

  // Global language subdomains
  "hi.doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "hi",
    supportedLocales: ["hi"],
    hreflangByLocale: { hi: "hi" },
  }),
  "ru.doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "ru",
    supportedLocales: ["ru"],
    hreflangByLocale: { ru: "ru" },
  }),
  "zh.doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "zh",
    supportedLocales: ["zh"],
    hreflangByLocale: { zh: "zh" },
  }),

  // Global language subdomains without local market pages yet
  "ko.doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "ko",
    supportedLocales: ["ko"],
    hreflangByLocale: { ko: "ko" },
  }),
  "uk.doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "uk",
    supportedLocales: ["uk"],
    hreflangByLocale: { uk: "uk" },
  }),
  "ja.doomum.com": definePublicDomain({
    marketCountryCode: GLOBAL_MARKET_CODE,
    defaultLocale: "ja",
    supportedLocales: ["ja"],
    hreflangByLocale: { ja: "ja" },
  }),

  // Country markets
  "doomum.co.uk": definePublicDomain({
    marketCountryCode: "GB",
    defaultLocale: "en",
    supportedLocales: ["en"],
    hreflangByLocale: { en: "en-GB" },
  }),
  "doomum.ro": definePublicDomain({
    marketCountryCode: "RO",
    defaultLocale: "ro",
    supportedLocales: ["ro"],
    hreflangByLocale: { ro: "ro-RO" },
  }),
  "doomum.de": definePublicDomain({
    marketCountryCode: "DE",
    defaultLocale: "de",
    supportedLocales: ["de"],
    hreflangByLocale: { de: "de-DE" },
  }),
  "doomum.fr": definePublicDomain({
    marketCountryCode: "FR",
    defaultLocale: "fr",
    supportedLocales: ["fr"],
    hreflangByLocale: { fr: "fr-FR" },
  }),
  "doomum.es": definePublicDomain({
    marketCountryCode: "ES",
    defaultLocale: "es",
    supportedLocales: ["es"],
    hreflangByLocale: { es: "es-ES" },
  }),
  "doomum.it": definePublicDomain({
    marketCountryCode: "IT",
    defaultLocale: "it",
    supportedLocales: ["it"],
    hreflangByLocale: { it: "it-IT" },
  }),
  "doomum.nl": definePublicDomain({
    marketCountryCode: "NL",
    defaultLocale: "nl",
    supportedLocales: ["nl"],
    hreflangByLocale: { nl: "nl-NL" },
  }),
  "doomum.be": definePublicDomain({
    marketCountryCode: "BE",
    defaultLocale: "nl",
    supportedLocales: ["nl"],
    hreflangByLocale: { nl: "nl-BE" },
  }),
  "doomum.pl": definePublicDomain({
    marketCountryCode: "PL",
    defaultLocale: "pl",
    supportedLocales: ["pl"],
    hreflangByLocale: { pl: "pl-PL" },
  }),
  "doomum.pt": definePublicDomain({
    marketCountryCode: "PT",
    defaultLocale: "pt",
    supportedLocales: ["pt"],
    hreflangByLocale: { pt: "pt-PT" },
  }),
  "doomum.dk": definePublicDomain({
    marketCountryCode: "DK",
    defaultLocale: "da",
    supportedLocales: ["da"],
    hreflangByLocale: { da: "da-DK" },
  }),
  "doomum.at": definePublicDomain({
    marketCountryCode: "AT",
    defaultLocale: "de",
    supportedLocales: ["de"],
    hreflangByLocale: { de: "de-AT" },
  }),
  "doomum.hu": definePublicDomain({
    marketCountryCode: "HU",
    defaultLocale: "hu",
    supportedLocales: ["hu"],
    hreflangByLocale: { hu: "hu-HU" },
  }),
  "doomum.gr": definePublicDomain({
    marketCountryCode: "GR",
    defaultLocale: "el",
    supportedLocales: ["el"],
    hreflangByLocale: { el: "el-GR" },
  }),
  "doomum.tr": definePublicDomain({
    marketCountryCode: "TR",
    defaultLocale: "tr",
    supportedLocales: ["tr"],
    hreflangByLocale: { tr: "tr-TR" },
  }),

  // Multilingual country markets
  "doomum.ae": definePublicDomain({
    marketCountryCode: "AE",
    defaultLocale: "ar",
    supportedLocales: ["ar", "en"],
    hreflangByLocale: {
      ar: "ar-AE",
      en: "en-AE",
    },
  }),
  "doomum.qa": definePublicDomain({
    marketCountryCode: "QA",
    defaultLocale: "ar",
    supportedLocales: ["ar", "en"],
    hreflangByLocale: {
      ar: "ar-QA",
      en: "en-QA",
    },
  }),

  // Country-market subdomains
  "no.doomum.com": definePublicDomain({
    marketCountryCode: "NO",
    defaultLocale: "no",
    supportedLocales: ["no"],
    hreflangByLocale: { no: "no-NO" },
  }),
  "fi.doomum.com": definePublicDomain({
    marketCountryCode: "FI",
    defaultLocale: "fi",
    supportedLocales: ["fi"],
    hreflangByLocale: { fi: "fi-FI" },
  }),
  "sv.doomum.com": definePublicDomain({
    marketCountryCode: "SE",
    defaultLocale: "sv",
    supportedLocales: ["sv"],
    hreflangByLocale: { sv: "sv-SE" },
  }),
  "bg.doomum.com": definePublicDomain({
    marketCountryCode: "BG",
    defaultLocale: "bg",
    supportedLocales: ["bg"],
    hreflangByLocale: { bg: "bg-BG" },
  }),
  "hr.doomum.com": definePublicDomain({
    marketCountryCode: "HR",
    defaultLocale: "hr",
    supportedLocales: ["hr"],
    hreflangByLocale: { hr: "hr-HR" },
  }),
  "cs.doomum.com": definePublicDomain({
    marketCountryCode: "CZ",
    defaultLocale: "cs",
    supportedLocales: ["cs"],
    hreflangByLocale: { cs: "cs-CZ" },
  }),
  "sk.doomum.com": definePublicDomain({
    marketCountryCode: "SK",
    defaultLocale: "sk",
    supportedLocales: ["sk"],
    hreflangByLocale: { sk: "sk-SK" },
  }),
  "sl.doomum.com": definePublicDomain({
    marketCountryCode: "SI",
    defaultLocale: "sl",
    supportedLocales: ["sl"],
    hreflangByLocale: { sl: "sl-SI" },
  }),
  "sr.doomum.com": definePublicDomain({
    marketCountryCode: "RS",
    defaultLocale: "sr",
    supportedLocales: ["sr"],
    hreflangByLocale: { sr: "sr-RS" },
  }),
};

export const AUTH_DOMAINS = ["id.doomum.com", "dev-id.doomum.com"] as const;

export const ALLOWED_AUTH_PAGES = ["/login", "/recover", "/authorize"] as const;

export function normalizeDomain(domain?: string | null) {
  return (domain || "doomum.com")
    .toLowerCase()
    .split(":")[0]
    .replace(/^www\./, "");
}

export function getPublicDomainConfig(domain?: string | null) {
  return domainConfig[normalizeDomain(domain)];
}

export function isPublicDomain(domain?: string | null) {
  return normalizeDomain(domain) in domainConfig;
}

export function isAuthDomain(domain?: string | null) {
  const normalized = normalizeDomain(domain);

  return AUTH_DOMAINS.includes(normalized as (typeof AUTH_DOMAINS)[number]);
}

export function getDefaultLocaleForDomain(domain?: string | null) {
  return getPublicDomainConfig(domain)?.defaultLocale || "en";
}

export function getMarketCountryCodeForDomain(domain?: string | null) {
  return getPublicDomainConfig(domain)?.marketCountryCode || GLOBAL_MARKET_CODE;
}

export function getSupportedLocalesForDomain(domain?: string | null) {
  return getPublicDomainConfig(domain)?.supportedLocales || ["en"];
}

export function isLocaleSupportedOnDomain({
  domain,
  locale,
}: {
  domain?: string | null;
  locale: string;
}) {
  return getSupportedLocalesForDomain(domain).includes(locale);
}

export function isDefaultLocaleForDomain({
  domain,
  locale,
}: {
  domain?: string | null;
  locale: string;
}) {
  return getDefaultLocaleForDomain(domain) === locale;
}

export function getVisibleLocalePrefix({
  domain,
  locale,
}: {
  domain?: string | null;
  locale: string;
}) {
  const config = getPublicDomainConfig(domain);

  if (!config) return "";

  if (!config.supportedLocales.includes(locale)) return "";

  return locale === config.defaultLocale ? "" : `/${locale}`;
}

export function withVisibleLocalePrefix({
  domain,
  locale,
  pathname,
}: {
  domain?: string | null;
  locale: string;
  pathname: string;
}) {
  const prefix = getVisibleLocalePrefix({ domain, locale });
  const cleanPathname = pathname.startsWith("/") ? pathname : `/${pathname}`;

  if (cleanPathname === "/") {
    return prefix || "/";
  }

  return `${prefix}${cleanPathname}`;
}

export type DomainPathLocaleResolution = {
  domain: string;
  locale: string;
  defaultLocale: string;
  marketCountryCode: string;
  visibleLocalePrefix: string;
  pathnameWithoutLocalePrefix: string;
  hasVisibleLocalePrefix: boolean;
  prefixStatus:
    | "none"
    | "supported-secondary"
    | "default-locale"
    | "unsupported-locale";
};

export function resolveDomainPathLocale({
  domain,
  pathname,
}: {
  domain?: string | null;
  pathname: string;
}): DomainPathLocaleResolution {
  const normalizedDomain = normalizeDomain(domain);
  const config =
    getPublicDomainConfig(normalizedDomain) || domainConfig["doomum.com"];

  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];
  const allKnownLocales = new Set(
    Object.values(domainConfig).flatMap((item) => item.supportedLocales),
  );

  if (firstSegment && allKnownLocales.has(firstSegment)) {
    const restPath =
      `/${segments.slice(1).join("/")}`.replace(/\/$/, "") || "/";

    if (!config.supportedLocales.includes(firstSegment)) {
      return {
        domain: normalizedDomain,
        locale: config.defaultLocale,
        defaultLocale: config.defaultLocale,
        marketCountryCode: config.marketCountryCode,
        visibleLocalePrefix: "",
        pathnameWithoutLocalePrefix: pathname,
        hasVisibleLocalePrefix: false,
        prefixStatus: "unsupported-locale",
      };
    }

    if (firstSegment === config.defaultLocale) {
      return {
        domain: normalizedDomain,
        locale: firstSegment,
        defaultLocale: config.defaultLocale,
        marketCountryCode: config.marketCountryCode,
        visibleLocalePrefix: `/${firstSegment}`,
        pathnameWithoutLocalePrefix: restPath,
        hasVisibleLocalePrefix: true,
        prefixStatus: "default-locale",
      };
    }

    return {
      domain: normalizedDomain,
      locale: firstSegment,
      defaultLocale: config.defaultLocale,
      marketCountryCode: config.marketCountryCode,
      visibleLocalePrefix: `/${firstSegment}`,
      pathnameWithoutLocalePrefix: restPath,
      hasVisibleLocalePrefix: true,
      prefixStatus: "supported-secondary",
    };
  }

  return {
    domain: normalizedDomain,
    locale: config.defaultLocale,
    defaultLocale: config.defaultLocale,
    marketCountryCode: config.marketCountryCode,
    visibleLocalePrefix: "",
    pathnameWithoutLocalePrefix: pathname || "/",
    hasVisibleLocalePrefix: false,
    prefixStatus: "none",
  };
}

/**
 * Compatibility map.
 * Later, src/i18n/routing.ts should import and re-export this instead of
 * hand-maintaining domainLocaleMap.
 */
export const domainLocaleMap: Record<string, string> = Object.fromEntries(
  Object.entries(domainConfig).map(([domain, config]) => [
    domain,
    config.defaultLocale,
  ]),
);

/**
 * Compatibility map.
 * Later, src/i18n/routing.ts should import and re-export this instead of
 * hand-maintaining domainCountryMap.
 */
export const domainCountryMap: Record<string, string> = Object.fromEntries(
  Object.entries(domainConfig).map(([domain, config]) => [
    domain,
    config.marketCountryCode,
  ]),
);

export const primaryDomainByLocale: Record<string, string> = {
  ar: "doomum.ae",
  bg: "bg.doomum.com",
  cs: "cs.doomum.com",
  da: "doomum.dk",
  de: "doomum.de",
  el: "doomum.gr",
  en: "doomum.com",
  es: "doomum.es",
  fi: "fi.doomum.com",
  fr: "doomum.fr",
  hi: "hi.doomum.com",
  hr: "hr.doomum.com",
  hu: "doomum.hu",
  it: "doomum.it",
  ja: "ja.doomum.com",
  ko: "ko.doomum.com",
  nl: "doomum.nl",
  no: "no.doomum.com",
  pl: "doomum.pl",
  pt: "doomum.pt",
  ro: "doomum.ro",
  ru: "ru.doomum.com",
  sk: "sk.doomum.com",
  sl: "sl.doomum.com",
  sr: "sr.doomum.com",
  sv: "sv.doomum.com",
  tr: "doomum.tr",
  uk: "uk.doomum.com",
  zh: "zh.doomum.com",
};

/**
 * Deprecated compatibility alias.
 * Replace SEO usage with domainConfig / hreflang helpers later.
 */
export const languageFallback = primaryDomainByLocale;

export function getDomainByLocale(locale: string): string {
  return primaryDomainByLocale[locale] || "doomum.com";
}
